import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  Car, 
  CheckCircle, 
  Download, 
  Loader2,
  AlertCircle,
  Home
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [dossier, setDossier] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const dossierId = searchParams.get('dossier_id');

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId || attempts >= 5) {
        if (attempts >= 5) {
          setStatus('timeout');
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          
          // Fetch dossier details
          if (dossierId) {
            const dossierResponse = await axios.get(`${API}/dossiers/${dossierId}`);
            setDossier(dossierResponse.data);
          }
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    };

    pollPaymentStatus();
  }, [sessionId, dossierId, attempts]);

  const downloadCerfa = async (cerfaType) => {
    if (!dossierId) return;
    
    try {
      const response = await axios.get(
        `${API}/dossiers/${dossierId}/cerfa/${cerfaType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cerfa_${cerfaType}_${dossierId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`CERFA ${cerfaType} téléchargé`);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Confirmation</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {status === 'loading' && (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Loader2 className="w-16 h-16 text-[#000091] animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">
                Vérification du paiement...
              </h2>
              <p className="text-slate-500">
                Merci de patienter quelques instants
              </p>
            </CardContent>
          </Card>
        )}

        {status === 'success' && (
          <Card className="border-green-300 bg-green-50/30" data-testid="success-card">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">
                Paiement confirmé !
              </h2>
              <p className="text-slate-600 mb-8">
                Votre dossier a été transmis à notre équipe. Vous recevrez un email de confirmation 
                avec les prochaines étapes.
              </p>

              {dossier && (
                <div className="bg-white rounded-lg border border-slate-200 p-6 mb-8 text-left">
                  <h3 className="font-semibold text-[#0F172A] mb-4">Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    {dossier.numero_dossier && (
                      <div className="flex justify-between items-center pb-2 mb-2 border-b border-slate-200">
                        <span className="text-slate-500">N° de dossier</span>
                        <span className="font-bold text-lg text-[#000091]" data-testid="dossier-number">{dossier.numero_dossier}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Véhicule</span>
                      <span className="text-[#0F172A]">{dossier.vehicle.marque} {dossier.vehicle.modele}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Formule</span>
                      <span className="text-[#0F172A]">
                        {dossier.package_type === 'premium' ? 'WW + Définitive' : 'Définitive'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Message de confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <p className="text-sm text-blue-800">
                  <strong>Prochaines étapes :</strong> Notre équipe va vérifier votre dossier et vos documents. 
                  Vous recevrez un email de confirmation une fois le traitement terminé.
                </p>
              </div>

              <Link to="/">
                <Button className="bg-[#000091] hover:bg-[#000091]/90" data-testid="back-home-btn">
                  <Home className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <Card className="border-red-300 bg-red-50/30" data-testid="error-card">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">
                {status === 'expired' ? 'Session expirée' : 'Erreur de vérification'}
              </h2>
              <p className="text-slate-600 mb-8">
                {status === 'expired' 
                  ? 'Votre session de paiement a expiré. Veuillez réessayer.'
                  : 'Nous n\'avons pas pu vérifier votre paiement. Si vous avez été débité, contactez-nous.'}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {dossierId && (
                  <Button 
                    onClick={() => navigate(`/recapitulatif/${dossierId}`)}
                    className="bg-[#000091] hover:bg-[#000091]/90"
                    data-testid="retry-payment-btn"
                  >
                    Réessayer le paiement
                  </Button>
                )}
                <Link to="/">
                  <Button variant="outline" data-testid="back-home-error-btn">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ConfirmationPage;
